<template>
    <div>
        <div class="demo-spin-article">
            <div class="row mb-3">
                <div class="col-md-6">
                    <label class="form-col-label control-label required">{{
                        $t("loanRejection.requestDate")
                    }}</label>
                    <DatePicker
                        :value="model.request_date"
                        placeholder="DD-MM-YYYY"
                        format="dd-MM-yyyy"
                        @on-change="onChangeRequestDate"
                        :options="options"
                        style="width: 100%"
                    ></DatePicker>
                    <div class="text-danger" v-if="errors.has('request_date')">
                        {{ errors.first("request_date") }}
                    </div>
                </div>
                <div class="col-md-6">
                    <label class="form-col-label control-label required">{{
                        $t("loanRejection.customer")
                    }}</label>
                    <Select
                        v-model="model.customer_id"
                        filterable
                        clearable
                        :placeholder="$t('loanRejection.customer')"
                        :class="{
                            'ivu-form-item-error': errors.has('customer_id')
                        }"
                    >
                        <Option
                            v-for="(option, index) in customers"
                            :value="option.customer_id"
                            :key="index"
                        >
                            {{ option.customer_name_en }} |
                            {{ option.customer_name_kh }}
                        </Option>
                    </Select>
                    <div class="text-danger" v-if="errors.has('customer_id')">
                        {{ errors.first("customer_id") }}
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-4">
                    <label class="form-col-label control-label">{{
                        $t("loanRejection.motorType")
                    }}</label>
                    <input
                        v-model.trim="model.motor_type"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.has('motor_type') }"
                    />
                    <div
                        class="invalid-feedback"
                        v-if="errors.has('motor_type')"
                    >
                        {{ errors.first("motor_type") }}
                    </div>
                </div>
                <div class="col-md-4">
                    <label class="form-col-label control-label required">{{
                        $t("loanRejection.requestedAmount")
                    }}</label>
                    <div class="tw-flex tw-space-x-2">
                        <InputNumber
                            v-model="model.requested_amount"
                            :min="0"
                            style="width: 60%"
                        ></InputNumber>
                        <Select
                            v-model="model.currency_id"
                            style="width: 40%"
                        >
                            <Option
                                v-for="(option, index) in currencies"
                                :value="option.currency_id"
                                :key="index"
                            >
                                {{ option.sign }} {{ option.currency_code }}
                            </Option>
                        </Select>
                    </div>
                </div>
                <div class="col-md-4">
                    <label class="form-col-label control-label">{{
                        $t("loanRejection.rejectedBy")
                    }}</label>
                    <Select
                        v-model="model.rejected_by_id"
                        clearable
                        :placeholder="$t('loanRejection.rejectedBy')"
                        :class="{
                            'ivu-form-item-error': errors.has('rejected_by_id')
                        }"
                    >
                        <Option
                            v-for="(option, index) in rejected_bys"
                            :value="option.rejected_by_id"
                            :key="index"
                        >
                            {{ option.rejected_by_name }}
                        </Option>
                    </Select>
                    <div
                        class="text-danger"
                        v-if="errors.has('rejected_by_id')"
                    >
                        {{ errors.first("rejected_by_id") }}
                    </div>
                </div>
            </div>
            <div class="mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">{{
                        $t("loanRejection.reason")
                    }}</label>
                    <textarea
                        v-model="model.reason"
                        class="form-control"
                        rows="3"
                        :class="{ 'is-invalid': errors.has('reason') }"
                    ></textarea>
                    <div class="invalid-feedback" v-if="errors.has('reason')">
                        {{ errors.first("reason") }}
                    </div>
                </div>
            </div>
            <div slot="footer">
                <div class="tw-flex tw-justify-end tw-space-x-2 tw-sh">
                    <ts-button
                        @click.prevent="() => $emit('cancel')"
                        class="btn-gray"
                    >
                        {{ $t("cancel") }}</ts-button
                    >
                    <ts-button
                        color="primary"
                        v-if="!isUpdate"
                        outline
                        :waiting="waiting_new"
                        :disabled="waiting"
                        @click.prevent="onSaveAddNew"
                    >
                        {{ $t("saveAddNew") }}</ts-button
                    >
                    <ts-button
                        color="primary"
                        v-if="!isUpdate"
                        :waiting="waiting"
                        :disabled="waiting_new"
                        @click.prevent="onSave"
                    >
                        {{ $t("save") }}</ts-button
                    >
                    <ts-button
                        v-if="isUpdate"
                        color="primary"
                        @click.prevent="onUpdate"
                        :waiting="waiting"
                    >
                        {{ $t("update") }}</ts-button
                    >
                </div>
            </div>
        </div>
        <Spin size="large" fix v-if="loading"> </Spin>
    </div>
</template>

<script>
import { Errors } from "form-backend-validation";
import { mapState } from "vuex";
import { isEmpty } from "lodash";
import moment from "moment";

export default {
    name: "loanRejectionForm",
    data() {
        return {
            errors: new Errors(),
            waiting: false,
            waiting_new: false,
            loading: false,
            model: {
                request_date: moment().format("DD-MM-YYYY"),
                customer_id: null,
                motor_type: null,
                requested_amount: null,
                rejected_by_id: null,
                reason: null
            },
            options: {
                disabledDate(date) {
                    return date.valueOf() > Date.now();
                }
            }
        };
    },
    computed: {
        ...mapState("creditOperation/loanRejection", [
            "edit_data",
            "customers",
            "rejected_bys",
            "currencies"
        ]),
        isUpdate() {
            return !isEmpty(this.edit_data);
        }
    },
    methods: {
        onChangeRequestDate(date) {
            this.model.request_date = date;
        },
        async fetchResource() {
            this.loading = true;
            await this.$store.dispatch(
                "creditOperation/loanRejection/getCustomers"
            );
            await this.$store.dispatch(
                "creditOperation/loanRejection/getRejectedBys"
            );
            await this.$store.dispatch(
                "creditOperation/loanRejection/getCurrencies"
            );
            this.setEditData();
            this.loading = false;
        },
        onSaveAddNew() {
            this.errors = new Errors();
            this.waiting_new = true;
            this.$store
                .dispatch("creditOperation/loanRejection/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.clearInput();
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting_new = false;
                });
        },
        onSave() {
            this.errors = new Errors();
            this.waiting = true;
            this.$store
                .dispatch("creditOperation/loanRejection/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("cancel");
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        onUpdate() {
            this.errors = new Errors();
            this.waiting = true;
            this.$store
                .dispatch("creditOperation/loanRejection/update", {
                    id: this.edit_data.rejection_id,
                    data: this.model
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("cancel");
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        setEditData() {
            this.errors = new Errors();
            if (!isEmpty(this.edit_data)) {
                this.model.request_date = this.edit_data.request_date;
                this.model.customer_id = this.edit_data.customer_id;
                this.model.motor_type = this.edit_data.motor_type;
                this.model.requested_amount = this.edit_data.requested_amount;
                this.model.rejected_by_id = this.edit_data.rejected_by_id;
                this.model.reason = this.edit_data.reason;
            }
        },
        clearInput() {
            this.model.request_date = moment().format("DD-MM-YYYY");
            this.model.customer_id = null;
            this.model.motor_type = null;
            this.model.requested_amount = null;
            this.model.rejected_by_id = null;
            this.model.reason = null;
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "LOAN REJECTION",
                desc: not.text
            });
        }
    }
};
</script>
